/**
 * ConfirmDialog — self-drawn confirmation dialog.
 *
 * Replaces platform-native confirm dialogs (Tauri dialog / browser confirm())
 * with a consistent React-rendered UI. Works identically across Editor, Web, and Play.
 */

import { AlertTriangle } from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import type { ReactNode } from "react";
import { DialogButton, DialogOverlay } from "./DialogOverlay";

export interface ConfirmDialogDetailItem {
    title: string;
    description?: string;
    meta?: string;
    tone?: "default" | "warning";
}

export interface ConfirmDialogProps {
    open: boolean;
    /** Dialog title (default: "确认") */
    title?: string;
    /** Message body text */
    message: string;
    /** Confirm button label (default: "确认") */
    confirmLabel?: string;
    /** Cancel button label (default: "取消") */
    cancelLabel?: string;
    /** Whether confirm is a destructive action (uses warning style) */
    destructive?: boolean;
    /** Structured detail rows rendered below the message. */
    detailItems?: ConfirmDialogDetailItem[];
    /** Optional muted note below detail rows. */
    footerNote?: string;
    onConfirm: () => void;
    onCancel: () => void;
}

export function ConfirmDialog({
    open,
    title,
    message,
    confirmLabel,
    cancelLabel,
    destructive = false,
    detailItems,
    footerNote,
    onConfirm,
    onCancel,
}: ConfirmDialogProps): ReactNode {
    const t = useT("editor");
    return (
        <DialogOverlay
            title={title ?? t("dialog.confirm")}
            open={open}
            onClose={onCancel}
            className="w-[520px]"
            footer={
                <>
                    <DialogButton onClick={onCancel}>
                        {cancelLabel ?? t("common.cancel")}
                    </DialogButton>
                    <DialogButton
                        variant="primary"
                        onClick={onConfirm}
                        className={destructive ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground" : undefined}
                    >
                        {confirmLabel ?? t("dialog.confirm")}
                    </DialogButton>
                </>
            }
        >
            <div className="flex items-start gap-3 px-4 py-4">
                {destructive && (
                    <div className="shrink-0 mt-0.5">
                        <AlertTriangle size={18} className="text-destructive" />
                    </div>
                )}
                <div className="min-w-0 flex-1 space-y-3">
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                        {message}
                    </p>
                    {detailItems && detailItems.length > 0 ? (
                        <div className="max-h-[240px] overflow-auto rounded-md border border-border bg-muted/30">
                            {detailItems.map((item, index) => (
                                <div
                                    key={`${item.title}-${index}`}
                                    className="border-b border-border/70 px-3 py-2 last:border-b-0"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 text-xs font-medium text-foreground">
                                            {item.title}
                                        </div>
                                        {item.meta ? (
                                            <div className="shrink-0 text-[10px] text-muted-foreground">
                                                {item.meta}
                                            </div>
                                        ) : null}
                                    </div>
                                    {item.description ? (
                                        <div
                                            className={
                                                item.tone === "warning"
                                                    ? "mt-1 break-all font-mono text-[11px] leading-relaxed text-destructive"
                                                    : "mt-1 break-all font-mono text-[11px] leading-relaxed text-muted-foreground"
                                            }
                                        >
                                            {item.description}
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    ) : null}
                    {footerNote ? (
                        <p className="text-[11px] leading-relaxed text-muted-foreground">
                            {footerNote}
                        </p>
                    ) : null}
                </div>
            </div>
        </DialogOverlay>
    );
}
